import { useEffect, useMemo } from 'react';
import { useLocation, useNavigate, useOutletContext, useParams, useSearchParams } from 'react-router-dom';
import { Role } from '@bloomays-lib/types.shared';
import MonthlyPrime from '../organisms/MonthlyPrime';
import { useAnnualTamPrimesStore, useAuthStore } from '../../stores';
import type { PrimesMonthlyNavigationState } from '../../types/primes';
import { referenceDateToYear } from '../../utils/annualPrime.utils';
import {
  buildAnnualTamPrimesCacheKey,
  deriveMonthlyTamPrimeFromAnnualAggregate,
} from '../../utils/annualTamPrimesCache.utils';
import {
  doesPreloadedMonthlyTamPrimeMatchReferenceMonth,
  getDefaultPrimeReferenceDate,
  isValidMonthYear,
  parseMonthYearToDate,
  parseMonthlyPrimeTamSearchParam,
} from '../../utils/monthlyPrime.utils';

export type PrimesOutletContext =
  | {
      tamEmail: string;
      referenceDate: Date;
    }
  | undefined;

const PrimesMonthlyPage = () => {
  const { mmYyyy } = useParams<{ mmYyyy?: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const outletContext = useOutletContext<PrimesOutletContext>();
  const auth = useAuthStore((state) => state.auth);
  const isSystem = auth?.user?.roles?.includes(Role.System) ?? false;

  const navigationState = location.state as PrimesMonthlyNavigationState | null;

  const tamEmailFromSearch = useMemo(
    () => parseMonthlyPrimeTamSearchParam(searchParams, isSystem),
    [isSystem, searchParams],
  );

  const tamEmail = tamEmailFromSearch ?? outletContext?.tamEmail;

  const referenceDateFromUrl = useMemo(() => {
    if (mmYyyy === undefined) {
      return undefined;
    }
    if (!isValidMonthYear(mmYyyy)) {
      return null;
    }
    return parseMonthYearToDate(mmYyyy);
  }, [mmYyyy]);

  const referenceDate = useMemo(
    () => referenceDateFromUrl ?? outletContext?.referenceDate ?? getDefaultPrimeReferenceDate(),
    [referenceDateFromUrl, outletContext?.referenceDate],
  );

  const cachedAnnualAggregate = useAnnualTamPrimesStore((state) => {
    const email = tamEmail?.trim();
    if (!email || referenceDateFromUrl === null) {
      return undefined;
    }

    const cacheKey = buildAnnualTamPrimesCacheKey(email, referenceDateToYear(referenceDate));
    return state.getCache(cacheKey)?.aggregate;
  });

  const preloadedMonthlyTamPrime = useMemo(() => {
    if (referenceDateFromUrl === null) {
      return undefined;
    }
    const monthlyTamPrimeFromNavigation = navigationState?.preloadedMonthlyTamPrime;
    if (monthlyTamPrimeFromNavigation !== undefined) {
      return doesPreloadedMonthlyTamPrimeMatchReferenceMonth(monthlyTamPrimeFromNavigation, referenceDate)
        ? monthlyTamPrimeFromNavigation
        : undefined;
    }

    if (cachedAnnualAggregate === undefined) {
      return undefined;
    }

    return deriveMonthlyTamPrimeFromAnnualAggregate(cachedAnnualAggregate, referenceDate);
  }, [cachedAnnualAggregate, navigationState?.preloadedMonthlyTamPrime, referenceDate, referenceDateFromUrl]);

  useEffect(() => {
    if (referenceDateFromUrl === null) {
      navigate('/primes/mois', { replace: true });
    }
  }, [navigate, referenceDateFromUrl]);

  if (referenceDateFromUrl === null) {
    return null;
  }

  return (
    <MonthlyPrime
      key={`${tamEmail ?? 'admin'}-${referenceDate.toISOString()}-${preloadedMonthlyTamPrime ? 'preloaded' : 'fetch'}`}
      tamEmail={tamEmail}
      referenceDate={referenceDate}
      preloadedMonthlyTamPrime={preloadedMonthlyTamPrime}
    />
  );
};

export default PrimesMonthlyPage;
